import { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { usePreferences } from '../../hooks/usePreferences'
import { checkLimit, incrementUsage, FREE_LIMITS } from '../../lib/usageLimits'
import type { RecipeInstructions } from '../../lib/recipeInstructions'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Ingredient {
  name:     string
  amount:   string
  category: string
}

interface RecipeResult {
  recipe_name:  string
  ingredients:  Ingredient[]
  instructions?: RecipeInstructions
}

interface MatchedIngredient extends Ingredient {
  inPantry:     boolean
  pantryItemId?: string
}

interface PantryItem {
  id:   string
  name: string
}

interface Props {
  householdId: string
  pantryItems: PantryItem[]
  onClose:     () => void
  onSuccess:   (msg: string) => void
  onSave?:     (name: string, ingredients: Ingredient[], instructions: RecipeInstructions) => Promise<void>
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function matchIngredientToPantry(
  ingredient: string,
  pantryItems: PantryItem[],
): { inPantry: boolean; pantryItemId?: string } {
  const ing = ingredient.toLowerCase().trim()
  const match = pantryItems.find(p => {
    const name = p.name.toLowerCase()
    return (
      name === ing ||
      name.includes(ing) ||
      ing.includes(name) ||
      name.includes(ing.replace(/s$/, '')) ||
      ing.includes(name.replace(/s$/, ''))
    )
  })
  return match
    ? { inPantry: true, pantryItemId: match.id }
    : { inPantry: false }
}

function getCategoryEmoji(category: string): string {
  switch (category) {
    case 'produce': return '🥬'
    case 'dairy':   return '🥛'
    case 'meat':    return '🥩'
    case 'bakery':  return '🍞'
    case 'pantry':  return '🫙'
    default:        return '📦'
  }
}

// ─── RecipeScanner ────────────────────────────────────────────────────────────

export default function RecipeScanner({ householdId, pantryItems, onClose, onSuccess, onSave }: Props) {
  const queryClient = useQueryClient()
  const navigate    = useNavigate()
  const { user }    = useAuth()
  const { isPremium } = usePreferences()
  const userId      = user?.id ?? null

  const [tab,          setTab]          = useState<'scan' | 'paste' | 'url'>('scan')
  const [scanning,     setScanning]     = useState(false)
  const [pasteText,    setPasteText]    = useState('')
  const [urlInput,     setUrlInput]     = useState('')
  const [result,       setResult]       = useState<RecipeResult | null>(null)
  const [matched,      setMatched]      = useState<MatchedIngredient[]>([])
  const [error,        setError]        = useState('')
  const [addingToList, setAddingToList] = useState(false)
  const [savingRecipe, setSavingRecipe] = useState(false)
  const [selected,     setSelected]     = useState<Set<number>>(new Set())
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Per-action usage. Camera scan AND paste-text both fire /api/claude-recipe
  // with the same shape (image-or-text → ingredients), so they share one
  // counter — otherwise a free user could just retype any recipe to bypass
  // the camera cap. URL import is a distinct user-facing affordance and
  // gets its own counter, matching the spec's split.
  const [scansUsed, setScansUsed] = useState(0)
  const [scansHit,  setScansHit]  = useState(false)
  const [urlsUsed,  setUrlsUsed]  = useState(0)
  const [urlsHit,   setUrlsHit]   = useState(false)

  useEffect(() => {
    if (isPremium || !userId) return
    ;(async () => {
      const scans = await checkLimit(userId, isPremium, 'recipe_scans')
      if (scans) { setScansUsed(scans.used); if (scans.hitLimit) setScansHit(true) }
      const urls = await checkLimit(userId, isPremium, 'recipe_url_imports')
      if (urls)  { setUrlsUsed(urls.used);   if (urls.hitLimit)  setUrlsHit(true)  }
    })()
  }, [isPremium, userId])

  // ── Process result from API ────────────────────────────────────────────────

  function processResult(data: RecipeResult) {
    setResult(data)
    const matchedIngredients = data.ingredients.map(ing => ({
      ...ing,
      ...matchIngredientToPantry(ing.name, pantryItems),
    }))
    setMatched(matchedIngredients)
    const missingIndices = new Set(
      matchedIngredients
        .map((m, i) => (!m.inPantry ? i : -1))
        .filter(i => i !== -1)
    )
    setSelected(missingIndices)
  }

  // ── Image scan ─────────────────────────────────────────────────────────────

  async function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (!isPremium && userId) {
      const r = await checkLimit(userId, isPremium, 'recipe_scans')
      if (r?.hitLimit) { setScansHit(true); return }
    }

    setScanning(true)
    setError('')
    setResult(null)

    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload  = () => resolve((reader.result as string).split(',')[1])
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      const response = await fetch('/api/claude-recipe', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ imageBase64: base64, mediaType: file.type || 'image/jpeg' }),
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Scan failed')
      if (!data.ingredients?.length) throw new Error('No ingredients found in image')
      processResult(data)
      await incrementUsage(userId, isPremium, 'recipe_scans')
      setScansUsed(c => c + 1)
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
    } finally {
      setScanning(false)
    }
  }

  // ── Text paste ─────────────────────────────────────────────────────────────

  async function handlePasteSubmit() {
    if (!pasteText.trim()) return

    if (!isPremium && userId) {
      const r = await checkLimit(userId, isPremium, 'recipe_scans')
      if (r?.hitLimit) { setScansHit(true); return }
    }

    setScanning(true)
    setError('')
    setResult(null)

    try {
      const response = await fetch('/api/claude-recipe', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ text: pasteText }),
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Could not parse recipe')
      if (!data.ingredients?.length) throw new Error('No ingredients found')
      processResult(data)
      await incrementUsage(userId, isPremium, 'recipe_scans')
      setScansUsed(c => c + 1)
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
    } finally {
      setScanning(false)
    }
  }

  // ── URL fetch ──────────────────────────────────────────────────────────────

  async function handleUrlSubmit() {
    if (!urlInput.trim()) return

    if (!isPremium && userId) {
      const r = await checkLimit(userId, isPremium, 'recipe_url_imports')
      if (r?.hitLimit) { setUrlsHit(true); return }
    }

    setScanning(true)
    setError('')
    setResult(null)

    try {
      const response = await fetch('/api/claude-recipe', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ text: `Recipe URL: ${urlInput}` }),
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Could not fetch recipe')
      if (!data.ingredients?.length) throw new Error('No ingredients found')
      processResult(data)
      await incrementUsage(userId, isPremium, 'recipe_url_imports')
      setUrlsUsed(c => c + 1)
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
    } finally {
      setScanning(false)
    }
  }

  // ── Add missing to shopping list ───────────────────────────────────────────

  async function handleAddToList() {
    const missing = matched.filter((_, i) => selected.has(i) && !matched[i].inPantry)
    if (!missing.length) return
    setAddingToList(true)

    try {
      let { data: activeList } = await supabase
        .from('shopping_lists')
        .select('id')
        .eq('household_id', householdId)
        .is('completed_at', null)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!activeList) {
        const { data: newList } = await supabase
          .from('shopping_lists')
          .insert({ household_id: householdId, generated_at: new Date().toISOString() })
          .select('id')
          .single()
        activeList = newList
      }

      if (!activeList) throw new Error('Could not create shopping list')

      for (const ing of missing) {
        const { data: existing } = await supabase
          .from('pantry_items')
          .select('id')
          .eq('household_id', householdId)
          .ilike('name', ing.name)
          .limit(1)
          .maybeSingle()

        let itemId = existing?.id

        if (!itemId) {
          const { data: newItem } = await supabase
            .from('pantry_items')
            .insert({
              household_id:       householdId,
              name:               ing.name.charAt(0).toUpperCase() + ing.name.slice(1),
              emoji:              getCategoryEmoji(ing.category),
              display_category:   ing.category || 'pantry',
              freshness_category: 'pantry_staple',
              is_active:          false,
              confirmation_count: 0,
              location:           'pantry',
              track_expiration:   false,
              status_override:    null,
            })
            .select('id')
            .single()
          itemId = newItem?.id
        } else {
          await supabase
            .from('pantry_items')
            .update({ status_override: null })
            .eq('id', itemId)
        }

        if (!itemId) continue

        const { data: existingListItem } = await supabase
          .from('list_items')
          .select('id')
          .eq('list_id', activeList.id)
          .eq('item_id', itemId)
          .neq('status', 'removed')
          .maybeSingle()

        if (!existingListItem) {
          await supabase.from('list_items').insert({
            list_id:       activeList.id,
            item_id:       itemId,
            status:        'pending',
            urgency_score: 50,
            sort_order:    0,
            is_one_off:    true,
            notes:         ing.amount || null,
          })
        }
      }

      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
      queryClient.invalidateQueries({ queryKey: ['latestList'] })
      queryClient.invalidateQueries({ queryKey: ['listItems'] })
      onSuccess(`${missing.length} ingredient${missing.length !== 1 ? 's' : ''} added to your list 🛒`)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Could not add to list')
    } finally {
      setAddingToList(false)
    }
  }

  const missingCount = matched.filter((m, i) => selected.has(i) && !m.inPantry).length
  const haveCount    = matched.filter(m => m.inPantry).length

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end items-center">

      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onClose}
      />

      {/* Sheet */}
      <motion.div
        className="relative bg-[#FAF8F4] rounded-t-3xl shadow-2xl w-full max-w-[480px]
                   max-h-[90vh] overflow-y-auto flex flex-col"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-[#E8E2DC]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-3 pb-4 border-b border-[#F0EDE8] shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-[#2C2A26]">Recipe Scanner</h2>
            <p className="text-xs text-[#9A9491] mt-0.5">See what you have and what you need</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full
                       bg-[#F0EDE8] text-[#7A7468] text-sm font-medium"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">

          {/* Only show tabs + input if no result yet */}
          {!result && (
            <>
              {/* Tabs */}
              <div className="flex gap-2 bg-[#F0EDE8] rounded-2xl p-1">
                <button
                  onClick={() => setTab('scan')}
                  className="flex-1 py-2 rounded-xl text-sm font-semibold transition-all"
                  style={{
                    background: tab === 'scan' ? 'white' : 'transparent',
                    color:      tab === 'scan' ? '#2C2A26' : '#9A9491',
                    boxShadow:  tab === 'scan' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  📷 Scan recipe
                </button>
                <button
                  onClick={() => setTab('paste')}
                  className="flex-1 py-2 rounded-xl text-sm font-semibold transition-all"
                  style={{
                    background: tab === 'paste' ? 'white' : 'transparent',
                    color:      tab === 'paste' ? '#2C2A26' : '#9A9491',
                    boxShadow:  tab === 'paste' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  ✏️ Paste text
                </button>
                <button
                  onClick={() => setTab('url')}
                  className="flex-1 py-2 rounded-xl text-sm font-semibold transition-all"
                  style={{
                    background: tab === 'url' ? 'white' : 'transparent',
                    color:      tab === 'url' ? '#2C2A26' : '#9A9491',
                    boxShadow:  tab === 'url' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  🔗 URL
                </button>
              </div>

              {/* Limit-hit upgrade panel — replaces the input area when the
                  active tab's counter is exhausted. Tab pills remain visible
                  so the user can hop to a tab that still has capacity. */}
              {!isPremium && (
                (tab === 'url' && urlsHit) ||
                ((tab === 'scan' || tab === 'paste') && scansHit)
              ) ? (
                <motion.div
                  key="limit"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-paper border border-border-sand rounded-2xl p-5 text-center"
                >
                  <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-2">
                    Limit reached
                  </p>
                  <h2 className="font-serif text-[20px] text-ink leading-[1.2] mb-2">
                    {tab === 'url'
                      ? <>Out of <em className="italic text-forest">URL imports</em></>
                      : <>Out of <em className="italic text-forest">recipe scans</em></>}
                  </h2>
                  <p className="text-sm text-ink-muted leading-[1.5] mb-1 max-w-[300px] mx-auto">
                    {tab === 'url'
                      ? `Free accounts can import ${FREE_LIMITS.recipe_url_imports} recipes from URL per month. You've used all ${FREE_LIMITS.recipe_url_imports} this month.`
                      : `Free accounts can scan ${FREE_LIMITS.recipe_scans} recipes per month. You've used all ${FREE_LIMITS.recipe_scans} this month.`}
                  </p>
                  <p className="text-xs text-ink-muted italic font-serif mb-5">
                    Resets on the 1st of next month.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/paywall')}
                    className="w-full rounded-2xl py-3 text-sm font-semibold text-white"
                    style={{ background: '#6B9E78' }}
                  >
                    Upgrade for unlimited
                  </button>
                </motion.div>
              ) : (
              <AnimatePresence mode="wait">
                {tab === 'url' ? (
                  <motion.div
                    key="url"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.15 }}
                    className="flex flex-col gap-3"
                  >
                    <input
                      type="url"
                      value={urlInput}
                      onChange={e => setUrlInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleUrlSubmit()}
                      placeholder="https://www.allrecipes.com/recipe/..."
                      className="w-full px-4 py-3 rounded-xl border border-[#E8E2DC] text-sm
                                 text-[#2C2A26] outline-none focus:border-[#6B9E78] bg-white"
                    />
                    <button
                      onClick={handleUrlSubmit}
                      disabled={scanning || !urlInput.trim()}
                      className="w-full py-3 rounded-xl text-sm font-semibold text-white
                                 disabled:opacity-50"
                      style={{ background: '#6B9E78' }}
                    >
                      {scanning ? 'Fetching recipe…' : 'Get ingredients'}
                    </button>
                    <p className="text-xs text-[#9A9491] text-center">
                      Paste a link from AllRecipes, Food Network, NYT Cooking, etc.
                    </p>
                  </motion.div>
                ) : tab === 'scan' ? (
                  <motion.div
                    key="scan"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.15 }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleImageSelect}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={scanning}
                      className="w-full rounded-2xl border-2 border-dashed border-[#6B9E78]
                                 flex flex-col items-center justify-center gap-3 py-10
                                 bg-[#E8F2EA]/40 hover:bg-[#E8F2EA] transition-colors
                                 disabled:opacity-50"
                    >
                      {scanning ? (
                        <>
                          <span className="text-4xl">⏳</span>
                          <p className="text-sm font-semibold text-[#6B9E78]">Analyzing recipe…</p>
                          <p className="text-xs text-[#9A9491]">This takes a few seconds</p>
                        </>
                      ) : (
                        <>
                          <span className="text-4xl">📷</span>
                          <p className="text-sm font-semibold text-[#2C2A26]">Take a photo or upload</p>
                          <p className="text-xs text-[#9A9491]">Recipe card, cookbook, or screenshot</p>
                        </>
                      )}
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="paste"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.15 }}
                    className="flex flex-col gap-3"
                  >
                    <textarea
                      value={pasteText}
                      onChange={e => setPasteText(e.target.value)}
                      placeholder={"Paste your recipe here...\n\ne.g. Ingredients:\n2 cups flour\n1 cup milk\n3 eggs..."}
                      rows={8}
                      className="w-full px-4 py-3 rounded-2xl border border-[#E8E2DC] text-sm
                                 text-[#2C2A26] outline-none resize-none focus:border-[#6B9E78]
                                 bg-white placeholder-[#C5BFB8]"
                    />
                    <button
                      onClick={handlePasteSubmit}
                      disabled={scanning || !pasteText.trim()}
                      className="w-full py-3 rounded-xl text-sm font-semibold text-white
                                 disabled:opacity-50 transition-colors"
                      style={{ background: '#6B9E78' }}
                    >
                      {scanning ? 'Analyzing…' : 'Check my pantry'}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
              )}

              {/* Free-tier remaining-uses badge, scoped to the active tab. */}
              {!isPremium && !(
                (tab === 'url' && urlsHit) ||
                ((tab === 'scan' || tab === 'paste') && scansHit)
              ) && (
                <p className="text-center text-[11px] italic font-serif text-ink-muted -mt-1">
                  {tab === 'url'
                    ? `${Math.max(0, FREE_LIMITS.recipe_url_imports - urlsUsed)} URL import${Math.max(0, FREE_LIMITS.recipe_url_imports - urlsUsed) === 1 ? '' : 's'} left this month`
                    : `${Math.max(0, FREE_LIMITS.recipe_scans - scansUsed)} recipe scan${Math.max(0, FREE_LIMITS.recipe_scans - scansUsed) === 1 ? '' : 's'} left this month`}
                </p>
              )}
            </>
          )}

          {/* Error */}
          {error && (
            <div className="bg-[#FAEAE8] rounded-2xl px-4 py-3">
              <p className="text-sm text-[#C25B4E] font-medium">{error}</p>
              <button
                onClick={() => { setError(''); setResult(null) }}
                className="text-xs text-[#C25B4E]/70 mt-1 hover:underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Results */}
          {result && matched.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col gap-4"
            >
              {/* Recipe name + summary */}
              <div className="bg-white rounded-2xl border border-[#F0EDE8] p-4">
                {result.recipe_name && (
                  <p className="text-sm font-bold text-[#2C2A26] mb-2">
                    🍳 {result.recipe_name}
                  </p>
                )}
                <div className="flex gap-3">
                  <div className="flex-1 bg-[#E8F2EA] rounded-xl px-3 py-2 text-center">
                    <p className="text-lg font-bold text-[#6B9E78]">{haveCount}</p>
                    <p className="text-xs text-[#6B9E78]">you have</p>
                  </div>
                  <div className="flex-1 bg-[#FAEAE8] rounded-xl px-3 py-2 text-center">
                    <p className="text-lg font-bold text-[#C25B4E]">
                      {matched.filter(m => !m.inPantry).length}
                    </p>
                    <p className="text-xs text-[#C25B4E]">you need</p>
                  </div>
                </div>
              </div>

              {/* Ingredient list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide">
                    Ingredients
                  </p>
                  {matched.some(m => !m.inPantry) && (
                    <button
                      onClick={() => {
                        const allMissing = new Set(
                          matched.map((m, i) => (!m.inPantry ? i : -1)).filter(i => i !== -1)
                        )
                        setSelected(prev => {
                          const allSelected = [...allMissing].every(i => prev.has(i))
                          return allSelected ? new Set() : allMissing
                        })
                      }}
                      className="text-xs font-semibold text-[#6B9E78] hover:underline"
                    >
                      {[...new Set(matched.map((m, i) => (!m.inPantry ? i : -1)).filter(i => i !== -1))].every(i => selected.has(i))
                        ? 'Deselect all'
                        : 'Select all missing'}
                    </button>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-[#F0EDE8] divide-y divide-[#F0EDE8] overflow-hidden">
                  {matched.map((ing, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-4 py-3"
                      onClick={() => {
                        if (ing.inPantry) return
                        setSelected(prev => {
                          const next = new Set(prev)
                          next.has(i) ? next.delete(i) : next.add(i)
                          return next
                        })
                      }}
                      style={{ cursor: ing.inPantry ? 'default' : 'pointer' }}
                    >
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-sm"
                        style={{
                          background: ing.inPantry
                            ? '#E8F2EA'
                            : selected.has(i)
                            ? '#FAEAE8'
                            : '#F2EDE6',
                        }}
                      >
                        {ing.inPantry ? '✓' : selected.has(i) ? '🛒' : '✗'}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#2C2A26] capitalize">{ing.name}</p>
                        {ing.amount && (
                          <p className="text-xs text-[#9A9491]">{ing.amount}</p>
                        )}
                      </div>

                      <span
                        className="text-xs font-semibold shrink-0"
                        style={{ color: ing.inPantry ? '#6B9E78' : '#C25B4E' }}
                      >
                        {ing.inPantry ? 'In pantry' : 'Need it'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructions captured — read-only notice. Pre-save editing
                  of instructions is out of scope; users can re-scan if the
                  AI got it wrong. */}
              {result.instructions && result.instructions.type === 'steps' && result.instructions.steps.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink-muted font-semibold mb-1">
                    Instructions
                  </p>
                  <p className="text-[11px] text-ink-muted italic font-serif mb-1">
                    {result.instructions.steps.length} step{result.instructions.steps.length === 1 ? '' : 's'} captured
                  </p>
                  <p className="text-[11px] text-ink-muted leading-[1.4]">
                    You'll see them in the recipe after saving.
                  </p>
                </div>
              )}
              {result.instructions && result.instructions.type === 'plain' && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink-muted font-semibold mb-1">
                    Instructions
                  </p>
                  <p className="text-[11px] text-ink-muted italic font-serif">
                    Captured as plain text. You'll see them in the recipe after saving.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-2 pb-4">
                {missingCount > 0 && (
                  <button
                    onClick={handleAddToList}
                    disabled={addingToList}
                    className="w-full py-3.5 rounded-xl text-sm font-semibold text-white
                               disabled:opacity-50 transition-colors"
                    style={{ background: '#6B9E78' }}
                  >
                    {addingToList
                      ? 'Adding…'
                      : `Add ${missingCount} item${missingCount !== 1 ? 's' : ''} to shopping list 🛒`}
                  </button>
                )}
                {onSave && result && (
                  <button
                    onClick={async () => {
                      setSavingRecipe(true)
                      try {
                        await onSave(
                          result.recipe_name || 'Untitled Recipe',
                          result.ingredients,
                          result.instructions ?? null,
                        )
                      } finally {
                        setSavingRecipe(false)
                      }
                    }}
                    disabled={savingRecipe}
                    className="w-full py-3 rounded-xl text-sm font-semibold disabled:opacity-50"
                    style={{ background: '#FAF8F4', color: '#6B9E78', border: '1.5px solid rgba(107,158,120,0.4)' }}
                  >
                    {savingRecipe ? 'Saving...' : '📖 Save recipe'}
                  </button>
                )}
                <button
                  onClick={() => { setResult(null); setMatched([]); setPasteText(''); setUrlInput(''); setError('') }}
                  className="w-full py-3 rounded-xl text-sm font-medium text-[#7A7468]
                             border border-[#E8E2DC]"
                >
                  Scan another recipe
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  )
}
